import { ArrowDown, ArrowUp, CalendarDays, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PMEmptyState } from "@/domains/pm/components/shared/PMEmptyState";
import { PMLoadingState } from "@/domains/pm/components/shared/PMLoadingState";
import { ProtocolIssueRow } from "@/domains/pm/components/issues/protocol/ProtocolIssueRow";
import { cn } from "@/lib/utils";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Language, Translations } from "@/lib/i18n/types";
import type { PMIssue } from "@/domains/pm/types";
import type { IssueSort, IssueSortField } from "@/domains/pm/utils/groupAndSort";
import { isProtocolFilterActive, type ProtocolIssueFilter } from "@/domains/pm/utils/protocol";
import { ProtocolIssueFilters } from "@/domains/pm/components/issues/protocol/ProtocolIssueFilters";
import type { ProtocolDataStatus } from "@/domains/pm/components/issues/protocol/useProtocolData";

type ProtocolCopy = Translations["pm"]["issues"]["protocol"];

interface ProtocolIssueStepProps {
  status: ProtocolDataStatus;
  /** The rows to show — already filtered and sorted by the parent, which owns
   *  that state because Select all and Shift-range must see the same list. */
  issues: PMIssue[];
  /** The whole period, for the filter's options and the "x of y" tally. */
  allIssues: PMIssue[];
  filter: ProtocolIssueFilter;
  /** Null until the user picks one: the rows then sit in the document's own
   *  order, and no header may claim to be sorting them. */
  sort: IssueSort | null;
  onFilterChange: (filter: ProtocolIssueFilter) => void;
  onSortChange: (field: IssueSortField) => void;
  included: ReadonlySet<number>;
  language: Language;
  copy: ProtocolCopy;
  onToggle: (id: number, shiftKey: boolean) => void;
  onToggleAll: () => void;
  onOpenIssue: (id: number) => void;
  onRetry: () => void;
}

/**
 * What sits under the heading: whichever of loading, failure, an empty period
 * and the picker itself the period's fetch has arrived at. They are mutually
 * exclusive states of one fetch, so they read as a sequence of guards rather
 * than as four independent conditions on the same block.
 */
function ProtocolIssueStepBody(props: ProtocolIssueStepProps) {
  const { status, issues, allIssues, copy } = props;
  if (status === "loading") {
    return <PMLoadingState label={copy.issuesLoading} className="py-12" />;
  }
  if (status === "error") {
    return (
      <div className="rounded-lg border border-destructive/40 p-4 text-center">
        <p className="text-sm text-muted-foreground">{copy.issuesError}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={props.onRetry}>
          {copy.retry}
        </Button>
      </div>
    );
  }
  if (status !== "ready") return null;
  if (issues.length === 0) {
    return <ProtocolIssuesEmpty copy={copy} periodSize={allIssues.length} />;
  }
  return <IssuePicker {...props} filtered={isProtocolFilterActive(props.filter)} />;
}

export function ProtocolIssueStep(props: ProtocolIssueStepProps) {
  const { status, allIssues, copy } = props;
  const { t } = useLanguage();

  return (
    <div className="space-y-3">
      {/* The filter rides on the heading row rather than claiming a band of its
          own: it is used once or twice per export, and the picker below it is
          what the step is for. */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{copy.issuesStep}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{copy.issuesStepHint}</p>
        </div>
        {status === "ready" && allIssues.length > 0 && (
          <ProtocolIssueFilters
            issues={allIssues}
            filter={props.filter}
            language={props.language}
            copy={copy}
            statusLabels={t.pm.issues.status}
            onChange={props.onFilterChange}
          />
        )}
      </div>

      <ProtocolIssueStepBody {...props} />
    </div>
  );
}

/**
 * Nothing to show, for one of two reasons.
 *
 * An empty period and a filter that matches nothing are different problems with
 * different fixes, so they never share a message: one says come back with a
 * wider period, the other says widen the filter.
 */
function ProtocolIssuesEmpty({ copy, periodSize }: { copy: ProtocolCopy; periodSize: number }) {
  if (periodSize === 0) {
    return (
      <PMEmptyState
        icon={CalendarDays}
        title={copy.noIssuesTitle}
        description={copy.noIssuesDescription}
        className="py-10"
      />
    );
  }
  return (
    <PMEmptyState
      icon={SlidersHorizontal}
      title={copy.noMatches}
      description={fill(copy.filteredCount, { count: "0", total: String(periodSize) })}
      className="py-10"
    />
  );
}

/** The tick-all bar and the rows under it, once a period has loaded issues. */
function IssuePicker({
  issues,
  included,
  copy,
  filtered,
  ...props
}: ProtocolIssueStepProps & { filtered: boolean }) {
  const { t } = useLanguage();
  const total = issues.length;
  // Counted over the VISIBLE rows, not `included.size`: under a filter the set
  // still holds the ticks of rows the user cannot see, and "12 of 4 selected"
  // is not a sentence. The period's own size is said separately below.
  const selectedCount = issues.reduce(
    (count, issue) => count + (included.has(issue.id) ? 1 : 0),
    0
  );
  // Fixed widths, because the layout must not depend on the content: an auto
  // table grows to fit the longest title, and the dialog clips rather than
  // scrolls, so the last column simply disappeared.
  const columns: Array<{ label: string; width: string; field: IssueSortField }> = [
    { label: t.pm.issues.detail.issue, width: "", field: "issue_number" },
    { label: t.pm.issues.detail.status, width: "w-[104px]", field: "status" },
    { label: t.pm.issues.detail.discipline, width: "w-[92px]", field: "discipline" },
    { label: t.pm.issues.detail.dueDate, width: "w-[88px]", field: "due_date" },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="flex cursor-pointer items-center gap-2 text-sm font-medium">
          <Checkbox
            checked={selectedCount === total}
            indeterminate={selectedCount > 0 && selectedCount < total}
            onCheckedChange={props.onToggleAll}
          />
          {fill(copy.selectAll, { count: String(total) })}
        </label>
        <span aria-live="polite" className="text-xs text-muted-foreground">
          {fill(copy.selectedCount, { count: String(selectedCount), total: String(total) })}
          {/* Under a filter "3 of 4" would read as the whole period, so the
              period's own size is said alongside it. Composed as nodes rather
              than an interpolated string: the separator is punctuation between
              two pieces of copy, not part of either. */}
          {filtered && (
            <>
              {" \u00b7 "}
              {fill(copy.filteredCount, {
                count: String(total),
                total: String(props.allIssues.length),
              })}
            </>
          )}
        </span>
      </div>
      {/* A plain overflow container rather than Radix ScrollArea: no
          ResizeObserver to stub in jsdom, and it nests cleanly inside
          DialogContent's own max-h/overflow. */}
      <div className="max-h-72 overflow-y-auto">
        {/* The issues list's own table: same column rhythm, same header
            treatment, same hairline under each row. */}
        <table className="w-full table-fixed border-collapse">
          {/* z-10 and an opaque cell background: rows hold positioned
              content (tick boxes, chips) that otherwise paints over a sticky
              header with no stacking order of its own. The rule is an inset
              shadow because a collapsed border scrolls away with the rows. */}
          <thead className="sticky top-0 z-10 bg-background shadow-[inset_0_-1px_0_hsl(var(--border)/0.6)]">
            <tr>
              <th className="w-8 bg-background px-3 py-1.5" />
              {columns.map((col) => {
                const active = props.sort?.field === col.field;
                const ascending = props.sort?.direction === "asc";
                return (
                  <th
                    key={col.label}
                    // The issues list's headers are bare clickable <th>s with
                    // no aria-sort and no button — reachable by mouse only.
                    // Same look, but a real control.
                    aria-sort={active ? (ascending ? "ascending" : "descending") : "none"}
                    className={cn("bg-background p-0", col.width)}
                  >
                    <button
                      type="button"
                      onClick={() => props.onSortChange(col.field)}
                      className="group flex w-full items-center gap-1 px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    >
                      {col.label}
                      {active ? (
                        <>
                          {ascending ? (
                            <ArrowUp aria-hidden className="h-3 w-3" />
                          ) : (
                            <ArrowDown aria-hidden className="h-3 w-3" />
                          )}
                          <span className="sr-only">
                            {ascending ? copy.sortAscending : copy.sortDescending}
                          </span>
                        </>
                      ) : (
                        // A hint that the header does something, before the
                        // first click reveals it.
                        <ArrowDown
                          aria-hidden
                          className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-40"
                        />
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {issues.map((issue) => (
              <ProtocolIssueRow
                key={issue.id}
                issue={issue}
                included={included.has(issue.id)}
                language={props.language}
                copy={copy}
                onToggle={props.onToggle}
                onOpen={props.onOpenIssue}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
